import { useRef, useState } from 'react';
import { toastService } from '../../../services/toastService';
import { deviceConfigApi, type BrandingSlot } from '../../../services/deviceConfigApi';
import { slotStatus, type SlotSummary } from './brandingLogic';

const TONE: Record<string, string> = {
  live: 'bg-emerald-500/15 text-emerald-600',
  staged: 'bg-amber-500/15 text-amber-600',
  none: 'bg-gray-100 text-gray-500',
};
const ACCEPT = 'image/png,image/jpeg,image/webp,video/mp4,video/webm';
/*
  THE BOOT SLOT ALSO TAKES A PREBUILT bootanimation.zip.

  It used to take only a video, which the service transcoded into the zip. That
  transcode is capped at 1366x768/15fps and builds the looping part1 from a
  single frame, so a boot animation made that way freezes on a still for the
  rest of the boot — while a hand-built zip carries its real resolution, frame
  rate and loop. Uploading the artifact directly skips the lossy step entirely.

  `.zip` is listed alongside the MIME types because browsers disagree about
  what they report for a zip: Chrome sends application/zip, Windows often sends
  application/x-zip-compressed, and some send nothing at all — in which case
  only the extension matches. The service accepts all three and validates the
  archive's contents rather than trusting any of them.
*/
const ACCEPT_BOOT = `${ACCEPT},application/zip,application/x-zip-compressed,.zip`;

function buildPreviewUrl(cdnDomain: string, slotSummary: SlotSummary): { url: string; isVideo: boolean } | null {
  if (!cdnDomain || !slotSummary?.processed) return null;
  const { file, poster } = slotSummary.processed as { file?: string; poster?: string };
  if (!file && !poster) return null;

  /*
    PREVIEW THE THING ITSELF WHEN A BROWSER CAN PLAY IT.

    The old rule was `!poster && assetType === 'video'` — the mere EXISTENCE of
    a poster suppressed video preview. That is right for the boot slot and
    wrong for the loading slot, and both have a poster, so NEITHER ever played:
    the panel showed a single still frame for an asset the operator had just
    uploaded as a video, with no way to see it move.

    The real question is not "is there a poster" but "can a <video> element
    play the processed file". The boot slot's processed artifact is
    `bootanimation.zip` — a zip of PNG frames plus desc.txt, which is what
    Android's bootanimation binary consumes and what no browser can render. Its
    poster IS the only preview possible. The loading slot's is `asset.mp4`,
    which plays perfectly.

    So: play the file when it is a playable container, fall back to the poster
    otherwise. Decided by the extension rather than by `assetType`, because
    `assetType` describes what was UPLOADED and the boot slot is uploaded as a
    video and processed into something that is not one.
  */
  const playable = !!file && /\.(mp4|webm|mov|m4v)$/i.test(file);
  const key = playable ? file! : (poster || file)!;
  return { url: `https://${cdnDomain}/${key}`, isVideo: playable };
}

export default function SlotUploadCard({
  slot, deviceType, title, subtitle, hint, slotSummary, onChanged, cdnDomain,
}: {
  slot: BrandingSlot; deviceType: string; title: string; subtitle: string; hint: string;
  slotSummary: SlotSummary; onChanged: () => void; cdnDomain: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const status = slotStatus(slotSummary);
  const hasAsset = !!(slotSummary?.version || slotSummary?.stagedVersion);
  const preview = buildPreviewUrl(cdnDomain, slotSummary);
  const isBoot = slot === 'boot';

  const upload = async (file: File) => {
    if (!file) return;
    setBusy(true);
    try {
      /*
        Some browsers report an empty `type` for a .zip. Presign validates the
        mime, so send the one the extension implies rather than letting the
        upload fail as "Unsupported type: ".
      */
      const isZip = /\.zip$/i.test(file.name);
      const mime = file.type || (isZip ? 'application/zip' : '');
      const { url } = await deviceConfigApi.presignUpload(deviceType, slot, mime, file.size);
      await deviceConfigApi.putToPresigned(url, file, mime);
      toastService.success(isZip ? `${title} uploaded — checking the archive…` : `${title} uploaded — transcoding…`);
      onChanged();
    } catch (e: any) {
      toastService.error(e?.message || `Upload failed`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setConfirmRemove(false);
    try {
      await deviceConfigApi.deleteSlot(deviceType, slot);
      toastService.success('Removed');
      onChanged();
    } catch (e: any) {
      toastService.error(e?.message || 'Remove failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-4 space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-extrabold text-gray-900">{title}</h3>
        <span className={`text-[10.5px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full ${TONE[status.tone]}`}>{status.text}</span>
        {hasAsset && !confirmRemove && (
          <button
            disabled={busy}
            onClick={() => setConfirmRemove(true)}
            className="ml-auto text-[10.5px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-500/15 text-red-500 hover:bg-red-500/25 transition-colors disabled:opacity-40"
          >
            Remove
          </button>
        )}
        {hasAsset && confirmRemove && (
          <span className="ml-auto flex items-center gap-1.5">
            <span className="text-[10.5px] text-gray-500">Sure?</span>
            <button
              disabled={busy}
              onClick={remove}
              className="text-[10.5px] font-bold uppercase px-2 py-0.5 rounded-full bg-red-500/20 text-red-500 hover:bg-red-500/30 transition-colors disabled:opacity-40"
            >
              Yes, remove
            </button>
            <button
              disabled={busy}
              onClick={() => setConfirmRemove(false)}
              className="text-[10.5px] font-bold uppercase px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
          </span>
        )}
      </div>
      <div className="text-[11px] text-gray-500 -mt-1">{subtitle}</div>

      {preview && (
        <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-100 flex items-center justify-center" style={{ height: 120 }}>
          {preview.isVideo ? (
            <video
              src={preview.url}
              muted
              loop
              playsInline
              autoPlay
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <img
              src={preview.url}
              alt={`${title} preview`}
              className="max-h-full max-w-full object-contain"
            />
          )}
        </div>
      )}

      <div
        onClick={() => !busy && inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) upload(f); }}
        className="border border-dashed border-gray-300 rounded-xl p-5 text-center cursor-pointer hover:border-emerald-500/60 transition-colors"
      >
        <div className="text-sm font-bold text-gray-600">
          {busy ? 'Uploading…' : isBoot ? 'Drop a bootanimation.zip, video or image' : 'Drop a video or image'}
        </div>
        <div className="text-[11px] text-gray-400 mt-1">
          {isBoot ? 'a prebuilt .zip is used as-is — a video is transcoded' : 'or click to choose'}
        </div>
      </div>
      <input ref={inputRef} type="file" accept={isBoot ? ACCEPT_BOOT : ACCEPT} className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
      <div className="text-[11px] text-gray-500 leading-relaxed">{hint}</div>
    </div>
  );
}
